import { useEffect, useState } from "react";
import {
  formatBytes,
  formatPlanBytes,
  PAYG_BYTES_PER_DOLLAR,
  PLANS,
} from "@saucer/core";
import { go, SaasFooter, SaasNav, useReveal } from "../components/saas-shell.js";
import { CliShowcase } from "../components/cli-showcase.js";
import { HugeiconsIcon, type IconSvgElement } from "@hugeicons/react";
import { AppleIcon, LinuxIcon, WindowsIcon } from "../components/platform-icons.js";
import {
  CpuIcon,
  FlashIcon,
  HardDriveIcon,
  Infinity01Icon,
  Link04Icon,
  Package01Icon,
  Robot01Icon,
} from "@hugeicons/core-free-icons";

const INSTALL = "curl -fsSL https://saucer.sh/install.sh | sh";

/**
 * The landing, cloned from the gitstat page structure the brief pointed at —
 * floating dark pill nav, huge centered type, the product framed as a big
 * screenshot card, narrow pricing column, FAQ accordion, rounded CTA card and
 * a columned footer — all repainted with saucer's own tokens.
 */
export function Landing() {
  useReveal();

  return (
    <div className="landing saas">
      <SaasNav />

      <section className="saas-hero">
        <span className="saas-beta">
          <i aria-hidden="true" /> Free to start, no card
        </span>
        <h1 className="saas-hero__title">
          Any file becomes{" "}
          <span className="landing__url" aria-label="saucer.sh/you/anything">
            saucer.sh/<CyclingName />/anything
          </span>
        </h1>
        <p className="saas-hero__lede">
          Move files between your laptop, your servers and your agents with one command —
          instead of scp, S3 presigns, or pasting into Slack.
        </p>
        <div className="saas-actions">
          <a className="pill pill--primary" href="/login" onClick={go("/login")}>
            Start free
          </a>
          <a className="saas-textlink" href="/docs" onClick={go("/docs")}>
            Read the docs →
          </a>
        </div>
        <InstallBar />
      </section>

      <div className="saas-wrap-5xl">
        <div className="saas-art">
          <div className="saas-frame">
            <CliShowcase />
          </div>
        </div>
      </div>

      <section className="saas-features" id="features">
        <div className="saas-wrap-5xl">
          <div className="saas-sectionhead">
            <span className="saas-chip">Why saucer</span>
            <h2 className="saas-h2">
              One binary.
              <br />
              Any file. A URL.
            </h2>
            <p className="saas-lede">
              A terminal-first way to move bytes — fast enough that you stop thinking
              about the transfer at all.
            </p>
          </div>

          <div className="bento">
            <article className="bento__cell bento__cell--feature">
              <CellHead icon={FlashIcon} label="The CLI" />
              <h3 className="bento__title">
                A native Zig binary that answers in <em>35&nbsp;ms</em>
              </h3>
              <p className="bento__copy">
                One static executable, nothing to install underneath it. The CLI holds a
                warm TLS connection in a background daemon and the API answers from the
                nearest edge, so the second command is as quick as the first.
              </p>
              <ul className="bento__tags">
                {PLATFORMS.map((platform) => (
                  <li key={platform} className="bento__tag mono">
                    {platform}
                  </li>
                ))}
              </ul>
              <div className="bento__bars">
                {LATENCY.map((bar) => (
                  <div key={bar.label} className="bento__bar">
                    <span className="bento__bar-label">{bar.label}</span>
                    <span className="bento__bar-value">{bar.value}</span>
                    <span className="bento__bar-track">
                      <i
                        className={bar.warm ? "bento__bar-fill bento__bar-fill--warm" : "bento__bar-fill"}
                        style={{ "--w": bar.ratio } as React.CSSProperties}
                      />
                    </span>
                  </div>
                ))}
              </div>
            </article>

            <article className="bento__cell">
              <CellHead icon={Infinity01Icon} label="Bandwidth" />
              <h3 className="bento__title">Unlimited, and never on a timer</h3>
              <p className="bento__copy">
                Nothing is metered but storage — a link that goes viral costs what a link nobody
                opens costs. Single files go up to {formatPlanBytes(PLANS.pro.maxFileBytes)}, and a
                link stays live until the day you delete it.
              </p>
            </article>

            <article className="bento__cell">
              <CellHead icon={Robot01Icon} label="Agents &amp; CI" />
              <h3 className="bento__title">Headless by default</h3>
              <p className="bento__copy">
                No browser, no prompts. Mint a token, set one variable — every file is a
                URL a script can curl.
              </p>
              <code className="bento__snippet mono">
                <span className="bento__snippet-prompt" aria-hidden="true">
                  $
                </span>
                SAUCER_TOKEN=… saucer send dist.zip
              </code>
            </article>

            {METRICS.map((metric) => (
              <article key={metric.label} className="bento__cell bento__cell--metric">
                <CellIcon icon={metric.icon} />
                <strong className="bento__metric">{metric.value}</strong>
                <span className="bento__metric-label">{metric.label}</span>
              </article>
            ))}
          </div>

          <p className="saas-micro saas-center">
            measured: `saucer whoami` from an M-series MacBook over Wi-Fi to production —
            median of 7 warm runs, median of 3 cold
          </p>
        </div>
      </section>

      <PricingCta />

      <section className="saas-faq" id="faq">
        <span className="saas-chip">FAQ</span>
        <h2 className="saas-h2">
          Frequently
          <br />
          asked questions
        </h2>
        <p className="saas-lede">Got questions? Answers about your files, links and the CLI.</p>

        <div className="saas-accordion">
          {FAQ.map((item) => (
            <details key={item.q} className="saas-accordion__item">
              <summary>{item.q}</summary>
              <p>{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="saas-cta">
        <div className="saas-wrap-5xl">
          <div className="saas-cta__card">
            <h2 className="saas-cta__title">
              A new era of
              <br />
              file sharing
            </h2>
            <p className="saas-lede">
              Your files are already good. Give them URLs under your own name.
            </p>
            <div className="saas-actions">
              <a className="pill pill--primary" href="/login" onClick={go("/login")}>
                Start free
              </a>
              <a className="pill pill--soft" href="/docs" onClick={go("/docs")}>
                See the docs
              </a>
            </div>
            <span className="saas-micro">
              {formatPlanBytes(PLANS.free.storageBytes)} free — no card, no invite
            </span>
          </div>
        </div>
      </section>

      <SaasFooter />
    </div>
  );
}

const NAMES = ["you", "your-agent", "ci", "that-server", "you"];

/** The hero's namespace cycles through everyone who gets a URL here. */
function CyclingName() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = window.setInterval(
      () => setIndex((current) => (current + 1) % NAMES.length),
      2400,
    );
    return () => window.clearInterval(timer);
  }, []);

  const name = NAMES[index]!;
  // ch ignores the display type's negative tracking; subtract it per glyph.
  const width = `calc(${name.length}ch - ${(name.length * 0.045).toFixed(3)}em)`;
  return (
    <em className="landing__you" style={{ width }}>
      <span key={index} className="landing__you-word">
        {name}
      </span>
    </em>
  );
}

/**
 * Sections rise into place as they enter the viewport. Everything is visible
 * without JS or with reduced motion — the class only ever adds the effect.
 */

/**
 * How you actually get the CLI, per platform.
 *
 * macOS and Linux both get the installer, because both have a prebuilt static
 * binary behind it — `public/dl` carries `saucer-macos-{arm64,x64}` and
 * `saucer-linux-{arm64,x64}`, and `install.sh` picks by `uname`. Windows is the
 * one `install.sh` explicitly refuses, so it gets the npm package, which is the
 * same CLI on a runtime instead of ahead of it.
 */
const INSTALL_TARGETS = [
  {
    id: "macos",
    label: "macOS",
    Icon: AppleIcon,
    command: INSTALL,
    note: "One static binary. No Node, no runtime underneath it.",
  },
  {
    id: "linux",
    label: "Linux",
    Icon: LinuxIcon,
    command: INSTALL,
    note: "Same installer, same static binary — arm64 and x64.",
  },
  {
    id: "windows",
    label: "Windows",
    Icon: WindowsIcon,
    command: "npx saucer-sh",
    note: "Needs Node 20+. No native Windows binary yet.",
  },
] as const;

type PlatformId = (typeof INSTALL_TARGETS)[number]["id"];

/**
 * Guesses which tab to open on. A visitor should see their own command without
 * touching anything, and the guess being wrong costs one click.
 *
 * `userAgentData.platform` where it exists, falling back to the UA string.
 * Deliberately checks Windows before Linux: Android UA strings contain "Linux"
 * too, and a phone is closer to wanting the npm line than the installer.
 */
function detectPlatform(): PlatformId {
  if (typeof navigator === "undefined") return "macos";

  const hinted = (
    navigator as Navigator & { userAgentData?: { platform?: string } }
  ).userAgentData?.platform;
  const source = `${hinted ?? ""} ${navigator.userAgent}`.toLowerCase();

  if (source.includes("win")) return "windows";
  if (source.includes("mac") || source.includes("iphone") || source.includes("ipad")) {
    return "macos";
  }
  if (source.includes("linux") || source.includes("android") || source.includes("x11")) {
    return "linux";
  }
  return "macos";
}

/** The dev-tool handshake: pick a platform, copy the one line it needs. */
function InstallBar() {
  const [platform, setPlatform] = useState<PlatformId>(detectPlatform);
  const [copied, setCopied] = useState(false);

  const active = INSTALL_TARGETS.find((entry) => entry.id === platform) ?? INSTALL_TARGETS[0];

  const copy = () => {
    navigator.clipboard.writeText(active.command).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1600);
      },
      () => undefined,
    );
  };

  return (
    <div className="installer">
      <div className="installer__tabs" role="tablist" aria-label="Your platform">
        {INSTALL_TARGETS.map((entry) => {
          const on = entry.id === platform;
          return (
            <button
              key={entry.id}
              role="tab"
              type="button"
              aria-selected={on}
              className={`installer__tab${on ? " installer__tab--on" : ""}`}
              onClick={() => {
                setPlatform(entry.id);
                setCopied(false);
              }}
            >
              <entry.Icon size={15} />
              {entry.label}
            </button>
          );
        })}
      </div>

      <div className="install mono">
        <span className="install__prompt" aria-hidden="true">
          $
        </span>
        <code className="install__cmd">{active.command}</code>
        <button className="install__copy" type="button" onClick={copy}>
          {copied ? "copied" : "copy"}
        </button>
      </div>

      <p className="installer__note meta">{active.note}</p>
    </div>
  );
}

/**
 * Every icon sits in a tile that fixes the glyph's box, so nothing depends on
 * what the icon set decides to emit — no overflow, no baseline drift.
 */
function CellIcon({ icon }: { icon: IconSvgElement }) {
  return (
    <span className="bento__icon" aria-hidden="true">
      <HugeiconsIcon icon={icon} size={24} strokeWidth={1.7} />
    </span>
  );
}

/** A cell's masthead: the tile, then the small mono label it belongs to. */
function CellHead({ icon, label }: { icon: IconSvgElement; label: string }) {
  return (
    <div className="bento__head">
      <CellIcon icon={icon} />
      <span className="bento__label">{label}</span>
    </div>
  );
}

// The four binaries actually published under /dl.
const PLATFORMS = ["macos · arm64", "macos · x64", "linux · arm64", "linux · x64"];

// Bars are drawn to scale against the slowest number in the set.
const LATENCY = [
  { label: "warm round-trip", value: "35 ms", ratio: 35 / 135, warm: true },
  { label: "cold start, TLS included", value: "~135 ms", ratio: 1, warm: false },
];

const METRICS = [
  { icon: HardDriveIcon, value: formatPlanBytes(PLANS.pro.maxFileBytes), label: "largest single file" },
  { icon: Package01Icon, value: "~1 MB", label: "static binary, zero deps" },
  { icon: CpuIcon, value: "0", label: "runtimes to install" },
  { icon: Link04Icon, value: "Forever", label: "link lifetime" },
];

const ENTERPRISE_EMAIL = "hey@saucer.sh";

/**
 * The landing used to print the whole free plan here — a price, a six-row
 * spec table, two buttons. It was /pricing rendered twice, which means two
 * places to update when a plan changes and one of them going stale. This is
 * the pointer instead: the headline number, the door to the real page, and
 * the one thing /pricing does not cover.
 */
function PricingCta() {
  return (
    <section className="saas-pricing" id="pricing">
      <div className="pcta">
        <div className="pcta__row">
          <div className="pcta__lead">
            <span className="saas-chip">Pricing</span>
            <h2 className="pcta__title">
              Start free. {formatPlanBytes(PLANS.free.storageBytes)}, no card.
            </h2>
            <p className="pcta__lede">
              Bandwidth is unlimited on every plan — storage is the only thing ever metered. Pro
              is ${PLANS.pro.price} a month for {formatPlanBytes(PLANS.pro.storageBytes)}.
            </p>
          </div>

          <div className="pcta__actions">
            <a className="pill pill--primary" href="/login" onClick={go("/login")}>
              Start free
            </a>
            <a className="pcta__compare" href="/pricing" onClick={go("/pricing")}>
              Compare plans →
            </a>
          </div>
        </div>

        {/* Inside the card, not floating under it — on a wide screen a lone
            centred sentence in that much empty space reads as a mistake. */}
        <p className="pcta__enterprise">
          Enterprise plans available — email{" "}
          <a href={`mailto:${ENTERPRISE_EMAIL}?subject=Enterprise%20plan`}>{ENTERPRISE_EMAIL}</a>.
        </p>
      </div>
    </section>
  );
}

const FAQ = [
  {
    q: "How do I get in?",
    a: "Sign in with GitHub. Your username becomes your namespace — saucer.sh/you/… — and the account is live immediately, on the free plan.",
  },
  {
    q: "Is it really free?",
    a: `The free plan is ${formatPlanBytes(PLANS.free.storageBytes)} with unlimited bandwidth, and it stays free. Pro is $${PLANS.pro.price} a month for ${formatPlanBytes(PLANS.pro.storageBytes)} and larger files, and extra storage is a dollar per ${formatBytes(PAYG_BYTES_PER_DOLLAR)}. Nothing else is metered.`,
  },
  {
    q: "What happens to a link if I rename the file?",
    a: "Links and files are separate: renaming a link is instant and the file behind it never moves. Deleting a file kills its link immediately.",
  },
  {
    q: "Can I lock a file?",
    a: "Add a password to any link. Browsers get an unlock page; scripts send an x-saucer-pw header, so the password never lands in logs or history.",
  },
  {
    q: "Is it safe to serve uploads from a link?",
    a: "Uploaded bytes are served with a strict content-security-policy and can never run as a page on the saucer origin.",
  },
  {
    q: "Does it work for agents and CI?",
    a: "Mint a token and set SAUCER_TOKEN — the CLI is fully headless. There's a ready-made skill at saucer.sh/skills/saucer/SKILL.md you can curl into Claude Code or AGENTS.md so your agent learns the whole workflow in one command.",
  },
  {
    q: "Why not just scp it?",
    a: "scp needs both machines up, reachable and keyed to each other. saucer needs one command on either side — the file lands at a URL that any machine, browser or agent can fetch.",
  },
  {
    q: "Can I use the links from scripts?",
    a: "Everything is a URL: curl the /raw path for bytes, add ?w=800&fmt=webp to any image link for an edge transform, pipe the CLI in CI.",
  },
];
